import _ from "lodash"

export default class ObjectUtils {
  static isFunction(obj) {
    return !!(obj && obj.constructor && obj.call && obj.apply)
  }

  /* static findDiffKeys (obj1, obj2) {
    if (!obj1 || !obj2) {
      return {}
    }

    return Object.keys(obj1)
      .filter((key) => !obj2.hasOwnProperty(key))
      .reduce((result, current) => {
        result[current] = obj1[current]

        return result
      }, {})
  } */

  static getJSXElement(obj, ...params) {
    return this.isFunction(obj) ? obj(...params) : obj
  }
}

/**
 * Compare 2 objects deep equality
 *
 * @param {object} obj1
 * @param {object} obj2
 * @returns
 */
export const areObjectsEqual = (obj1 = {}, obj2 = {}) => {
  if (!obj1 || !obj2) {
    throw new Error("Invalid objects")
  }
  return _.isEqual(obj1, obj2)
}

export const isObjectEmpty = (obj) => {
  if (!obj) {
    throw new Error("Invalid object")
  }
  return _.isEmpty(obj)
}

/**
 * @template T
 * @template {string|number|symbol} K
 *
 * @param {Record<K, T>} obj
 * @param {((item: T) => string | string[]) | Extract<keyof T, string> | Array<Extract<keyof T, string>>} accessor
 * @param {string} query
 * @returns {Record<K, T>}
 */
export function objectSearch(obj, accessor, query) {
  if (!query) return obj

  const transform = /** @param {string} str */ (str) => str.toLowerCase()

  const access = /** @param {T} item */ (item) =>
    typeof accessor === "function" ? accessor(item)
    : Array.isArray(accessor) ?
      accessor.map((_accessor) => dataGet(item, _accessor, ""))
    : dataGet(item, accessor, "")

  const results = /** @type {Record<K, T>} */ ({})

  for (const [key, value] of Object.entries(obj)) {
    let comparables = access(value)

    if (!Array.isArray(comparables)) comparables = [comparables]

    if (
      // @ts-expect-error
      comparables.some((comparable) =>
        transform(comparable).includes(transform(query))
      )
    ) {
      // @ts-ignore
      results[key] = value
    }
  }

  return results
}

/**
 * @template T
 * @template {string|number|symbol} K
 *
 * @param {Record<K, T>} obj
 * @param {((item: T) => string | string[]) | Extract<keyof T, string> | Array<Extract<keyof T, string>>} accessor
 * @param {string} query
 * @returns {T | undefined}
 */
export function objectFirst(obj, accessor, query) {
  return Object.values(objectSearch(obj, accessor, query))[0] ?? undefined
}

/**
 * @template T
 * @template {string|number|symbol} K
 *
 * @param {Record<K, T>} obj
 * @param {((item: T) => string | string[]) | Extract<keyof T, string> | Array<Extract<keyof T, string>>} accessor
 * @param {string} query
 * @returns {T | undefined}
 */
export function objectLast(obj, accessor, query) {
  const values = Object.values(objectSearch(obj, accessor, query))
  return values[values.length - 1] ?? undefined
}

/**
 * @template T
 * Undots properties of an object.
 *
 * @example
 * const result = undot({
 *  'a.b.c': 1,
 *  'a.b.d': 2,
 *  'a.e': 3,
 *  'f': 4,
 * })
 *
 * // result will be:
 * {
 *  a: {
 *      b: {
 *          c: 1,
 *          d: 2
 *      },
 *      e: 3
 *  },
 *  f: 4
 * }
 *
 * @param {T} obj
 * @returns {Undot<T>}
 */
export function undot(obj) {
  for (const key in obj) {
    const value = obj[key]
    const hasDot = key.includes(".")

    if (!hasDot) continue

    dataSet(obj, key, value)
    delete obj[key]
  }

  return /** @type {Undot<T>} */ (obj)
}

/**
 * @template T
 *
 * Converts an object to a dot notation string.
 *
 * @example
 * const result = dot({
 *  a: {
 *      b: {
 *          c: 1,
 *          d: 2
 *      },
 *      e: 3
 *  },
 *  f: 4
 * })
 *
 * // result will be:
 * {
 *  'a.b.c': 1,
 *  'a.b.d': 2,
 *  'a.e': 3,
 *  'f': 4,
 * }
 * @param {T} obj
 *
 * @returns {Dotified<T>}
 */
export function dot(obj) {
  const result = /** @type {Dotified<T>} */ ({})

  /**
   * @param {any} current
   * @param {unknown} property
   */
  function recurse(current, property) {
    if (Object(current) !== current) {
      // @ts-expect-error
      result[property] = current
    } else {
      for (const p in current) {
        recurse(current[p], property ? `${property}.${p}` : p)
      }
    }
  }

  recurse(obj, "")

  return result
}

/**
 * @template T
 * @template {DotNotationPath<T> | (string & {})} P
 * @param {T} obj
 * @param {P} key
 * @param {any} [defaultValue]
 * @returns {ValueAtPath<T, P> | undefined}
 */
export function dataGet(obj, key, defaultValue = undefined) {
  // @ts-expect-error
  return key.split(".").reduce((o, i) => o?.[i], obj) ?? defaultValue
}

/**
 * @template T
 * @template {string} K
 * @template TValue
 * Set object property by string path using dot notation
 *
 * @param {T} obj
 * @param {K} key
 * @param {TValue} value
 * @returns {SetNested<T, K, TValue>}
 */
export function dataSet(obj, key, value) {
  const keys = key.split(".")
  let current = obj

  for (let i = 0; i < keys.length; i++) {
    const k = keys[i]

    if (i === keys.length - 1) {
      // @ts-expect-error
      current[k] = value
    } else {
      // @ts-expect-error
      current[k] = current[k] || {}
    }

    // @ts-expect-error
    current = current[k]
  }

  return /** @type {SetNested<T, K, TValue>} */ (obj)
}

/**
 * @template T
 * @template {keyof T} K
 *
 * @param {T} obj - The object to exclude keys from.
 * @param {K | K[]} keys - The key(s) to exclude.
 * @return {Omit<T, K>} A new object with the specified keys excluded.
 */
export function excludes(obj, keys) {
  const rest = { ...obj }

  if (!Array.isArray(keys)) {
    keys = [keys]
  }

  keys.forEach((k) => delete rest[k])

  return rest
}
